import {
  Component,
  signal,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  effect,
  inject,
} from '@angular/core';
import { ContextService } from '../../services/context.service';
import {
  AssignmentDto,
  AssignmentSubtableDataDto,
  AssignmentService,
  AssignmentType,
  GetAssignmentDto,
  GetAssignmentsDto,
} from '../../shared/generated/backend';
import {
  DxBulletModule,
  DxCheckBoxModule,
  DxDateRangeBoxModule,
  DxDropDownBoxModule,
  DxDropDownButtonModule,
  DxLoadIndicatorModule,
  DxPopupModule,
  DxRadioGroupModule,
  DxTemplateModule,
  DxToastModule,
  DxTreeViewComponent,
  DxTreeViewModule,
} from 'devextreme-angular';
import { DxDataGridComponent, DxDataGridModule } from 'devextreme-angular/ui/data-grid';
import {
  Column,
  RowDblClickEvent,
  RowExpandedEvent,
  RowPreparedEvent,
  SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { CommonModule } from '@angular/common';
import { DxSelectBoxModule, DxButtonModule } from 'devextreme-angular';
import { LoaderComponent } from '../../loader/loader.component';
import { SharedModule } from '../../shared/shared.module';
import { firstValueFrom } from 'rxjs';
import notify from 'devextreme/ui/notify';
import { DxoSortingModule } from 'devextreme-angular/ui/nested';
import { DxTreeViewTypes } from 'devextreme-angular/ui/tree-view';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { UtilsService } from '../../services/utils.service';
import dxDataGrid from 'devextreme/ui/data_grid';
import { DragStartEvent, DragEndEvent } from 'devextreme/ui/draggable';
import { PlanningHelperService } from '../../services/planning-helper.service';
import { TranslateService } from '@ngx-translate/core';
import { AssignmentHelper } from '../../shared/util/assignment-helper';
import { DateRangeStore } from '../../services/date-range.service';
import { MathHelper } from '../../shared/util/math-helper';
import { createKeyedBusyState } from '../../shared/util/busy-state';
import { Router } from '@angular/router';
import { format } from 'date-fns';
import { AssignmentInfoBadgesComponent } from '../../shared/components/assignment-info-badges/assignment-info-badges.component';
import {
  AssignmentExtendedDto,
  effectivePrimeLogisticEventDate,
  isFarAway,
  isMainPrimeLogisticAssignment,
  isMiOk,
  isMiProbSolutions,
  isMiWarning,
  showEventTime,
  toAssignmentExtendedDto,
} from '../../shared/util/assignment-extended';
import { AssignmentListDataGridComponent } from '../assignment-list-data-grid/assignment-list-data-grid.component';
import { NotificationService } from '../../shared/services/notification.service';

interface State {
  id: number;
  text: string;
  filter: (d: AssignmentExtendedDto) => boolean;
}
interface GridKey {
  AssignmentNo: number;
  Type: AssignmentType;
}
interface SortKey {
  value: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  farAway: boolean;
  kind: 'sortkey';
}
@Component({
  selector: 'app-assignment-list',
  // declarations: [DispoWeightPipe],
  imports: [
    // DispoWeightPipe,
    CommonModule,
    SharedModule,
    DxDataGridModule,
    DxTemplateModule,
    DxBulletModule,
    DxDateRangeBoxModule,
    DxPopupModule,
    DxSelectBoxModule,
    DxButtonModule,
    DxCheckBoxModule,
    DxRadioGroupModule,
    DxToastModule,
    LoaderComponent,
    DxLoadIndicatorModule,
    DxoSortingModule,
    DxDropDownButtonModule,
    DxTreeViewModule,
    DxDropDownBoxModule,
    IconComponent,
    AssignmentInfoBadgesComponent,
    AssignmentListDataGridComponent,
  ],
  templateUrl: './assignment-list.component.html',
  styleUrl: './assignment-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentListComponent implements OnInit {
  private contextService = inject(ContextService);
  private assignmentService = inject(AssignmentService);
  private planningHelperService = inject(PlanningHelperService);
  public utilService = inject(UtilsService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private translationService = inject(TranslateService);
  private dateRangeStore = inject(DateRangeStore);
  private router = inject(Router);
  private notificationService = inject(NotificationService);

  readonly visibilityBusy = createKeyedBusyState<string>();

  readonly assignments = signal(new Array<AssignmentExtendedDto>());
  _assignments = new Array<AssignmentExtendedDto>();
  dateRange!: [Date, Date];
  isLoading = signal(false);
  readonly AssignmentType = AssignmentType;
  protected showSearchAssignmentDialog = signal(false);
  windowRef = window;

  // selection states
  @ViewChild('stateTree', { static: false }) treeView!: DxTreeViewComponent;
  @ViewChild('infoTree', { static: false }) infoTreeView!: DxTreeViewComponent;
  @ViewChild(DxDataGridComponent, { static: false }) dataGrid!: DxDataGridComponent;
  states: Array<State> = [
    {
      id: 0,
      text: this.translationService.instant('ASSIGNMENT.STATE.OPEN'),
      filter: d => this.isOpen(d) && !this.isHidden(d),
    },
    {
      id: 1,
      text: this.translationService.instant('ASSIGNMENT.STATE.PLANNED'),
      filter: d => !this.isOpen(d),
    },
    {
      id: 2,
      text: this.translationService.instant('ASSIGNMENT.STATE.HIDDEN'),
      filter: d => this.isHidden(d),
    },
  ];

  infoFilters: Array<{
    id: number;
    text: string;
    filter: (d: AssignmentExtendedDto) => boolean;
  }> = [
    {
      id: 0,
      text: this.translationService.instant('ASSIGNMENT.WEIT_WEG'),
      filter: d => this.isFarAway(d),
    },
    {
      id: 1,
      text: this.translationService.instant('ASSIGNMENT.MIAUFTRAGSTATUS.1'),
      filter: d => this.isMiWarning(d),
    },
    {
      id: 2,
      text: this.translationService.instant('ASSIGNMENT.MIAUFTRAGSTATUS.2'),
      filter: d => this.isMiOk(d),
    },
    {
      id: 3,
      text: this.translationService.instant('ASSIGNMENT.INFOFILTER.MI_PROB_SOLUTIONS'),
      filter: d => this.isMiProbSolutions(d),
    },
    {
      id: 4,
      text: this.translationService.instant('ASSIGNMENT.INFOFILTER.PRIME_LOGISTIC'),
      filter: d => this.isMainPrimeLogisticAssignment(d),
    },
    {
      id: 5,
      text: this.translationService.instant('ASSIGNMENT.INFOFILTER.ohneInfo'),
      filter: d =>
        !this.isFarAway(d) &&
        !this.isMiWarning(d) &&
        !this.isMiOk(d) &&
        !this.isMiProbSolutions(d) &&
        !this.isMainPrimeLogisticAssignment(d),
    },
  ];

  isOpen(assignment: AssignmentExtendedDto): boolean {
    if (!assignment.IsDone) {
      return true;
    }
    const hasRemainingVolume =
      assignment.RemainingDispoVol !== undefined && MathHelper.roundTo4(assignment.RemainingDispoVol!) > 0;
    const hasRemainingWeight =
      assignment.RemainingDispoWeight !== undefined && MathHelper.ceilTo4(assignment.RemainingDispoWeight!) > 0;
    return hasRemainingVolume || hasRemainingWeight;
  }

  /**
   * Whether the hide/show toggle (Feature 1114) applies to this row: only open partial (TEIL)
   * deliveries that are completely planned in transport projects which are all at least
   * 'fixiert' (CanBeHidden, computed by the gateway). Pickups and planned rows can never be hidden.
   */
  canBeHidden(assignment: AssignmentExtendedDto): boolean {
    return (
      assignment.Type === AssignmentType.AssignmentDelivery &&
      assignment.CanBeHidden === true &&
      this.isOpen(assignment)
    );
  }

  /**
   * FilterState is stored on the order header (MAUFKO.BEARBEITUNGSSTATUS) and is therefore
   * also delivered on the pickup row of the same assignment number. It must only take effect
   * for rows that can (still) be hidden: the pickup of a hidden delivery and planned rows stay
   * visible, and a delivery reappears once one of its projects is set back below 'fixiert'.
   */
  isHidden(assignment: AssignmentExtendedDto): boolean {
    return assignment.FilterState === 1 && this.canBeHidden(assignment);
  }

  selectedStates = [0]; // default state is 'Offen' and 'Prime Logistic'
  selectedInfoFilters = [0, 1, 2, 3, 4, 5]; // default is all info filters selected
  selectedRows: AssignmentDto[] = [];
  firstSelectedRowDate: Date | null = null;

  stats: {
    totalDeliveryWeight: number;
    totalDeliveryVolume: number;
    totalPickupWeight: number;
    totalPickupVolume: number;
  } = { totalDeliveryVolume: 0, totalDeliveryWeight: 0, totalPickupVolume: 0, totalPickupWeight: 0 };

  constructor() {
    this.dateRange = this.dateRangeStore.range();
    effect(() => {
      this.dateRange = this.dateRangeStore.range();
    });
  }

  ngOnInit() {
    // Only open the default side panel if the URL doesn't already have one
    const tree = this.router.parseUrl(this.router.url);
    const hasRight = !!tree.root.children['right'];
    if (!hasRight) {
      this.contextService.openContextView('project');
    }

    this.getAssignments();
    this.registerForPlanningUpdates();
  }

  onDragStart = (e: DragStartEvent) => {
    const grid = e.component as dxDataGrid;
    const selectedIds = grid.getSelectedRowKeys();

    // Check if the dragged item is part of the current selection
    const draggedItemKey = grid.keyOf(e.itemData);
    const isDraggedItemSelected = selectedIds.some(key => {
      // Compare composite keys (AssignmentNo and Type)
      return JSON.stringify(key) === JSON.stringify(draggedItemKey);
    });

    if (selectedIds.length > 0 && isDraggedItemSelected) {
      // If dragging a selected item and there are selections, use all selected rows
      e.itemData = grid.getSelectedRowsData();
    } else {
      // If dragging a non-selected item, clear selection and use only the dragged row
      grid.clearSelection();
      grid.selectRows([draggedItemKey], false);
      e.itemData = [e.itemData];
    }

    // Restriction: two PrimeLogistics assignments cannot be planned simultaneously.
    // Stop the drag if the drag payload contains more than one PL assignment.
    const draggedRows: AssignmentDto[] = Array.isArray(e.itemData) ? e.itemData : [e.itemData];
    const plCount = draggedRows.filter(row => this.isMainPrimeLogisticAssignment(row)).length;
    if (plCount > 1) {
      e.cancel = true;
      notify(
        {
          message: this.translationService.instant('ASSIGNMENT.NOTIFICATION.ONLY_ONE_PRIME_LOGISTIC'),
          shading: false,
        },
        'warning',
        3000
      );
    }
  };

  onDragEnd = (e: DragEndEvent) => {
    // console.log('onDragEnd', e.toComponent.element().id);
    if (e.toComponent.element().id !== 'project-planning-list') return;
    this.markPlanning(e.itemData);
    this.registerForPlanningUpdates();
    // we do not start the planning here, it is started as the assignment is dropped into the planning area
  };

  private dispoWeightInOverview(item: AssignmentDto): number {
    return item.DispoWeight || 0;
  }

  private dispoVolumeInOverview(item: AssignmentDto): number {
    return item.DispoVolume || 0;
  }

  public isPlanningInProgress(): boolean {
    return this.planningHelperService.planningInProgress;
  }

  async onAddToPlanningClick(): Promise<void> {
    if (this.isPlanningInProgress()) return;
    const selection: AssignmentDto[] = this.selectedRows; // get the selected rows from first marked to last

    // Restriction: two PrimeLogistics assignments cannot be planned simultaneously.
    if (selection.filter(row => this.isMainPrimeLogisticAssignment(row)).length > 1) {
      notify(
        {
          message: this.translationService.instant('ASSIGNMENT.NOTIFICATION.ONLY_ONE_PRIME_LOGISTIC'),
          shading: false,
        },
        'warning',
        3000
      );
      return;
    }

    this.markPlanning(selection);
    this.registerForPlanningUpdates();
    await this.planningHelperService.planAssignments(selection);
  }

  onRowPrepared(e: RowPreparedEvent): void {
    if (e.rowType === 'data') {
      // Apply far away styling
      if (e.data.TransportLabel === 'FarAway') {
        e.rowElement.classList.add('far-away-row');
      }

      // Highlight rows with the same EventDate as the first selected row
      if (this.firstSelectedRowDate && e.data.EventDate) {
        const rowDate = new Date(e.data.EventDate);
        rowDate.setHours(0, 0, 0, 0); // Normalize to start of day for comparison

        if (rowDate.getTime() === this.firstSelectedRowDate.getTime()) {
          e.rowElement.classList.add('same-date-row');
        }
      }
    }
  }

  registerForPlanningUpdates() {
    // register for planning updates in order to reload changed assignments
    this.planningHelperService.onPlanningFinished().subscribe({
      next: ret => {
        if (ret.Operation === 'planning-removed') {
          this.getAssignments();
        }
        // here we handle: reloading of assignment, coloring of row
        const assignment = this._assignments.find(a => a.AssignmentNo == ret.AssignmentNo && a.Type == ret.Type);
        if (assignment) {
          const assignmentNo = assignment.AssignmentNo;
          const type = assignment.Type;
          if (assignmentNo !== undefined && type !== undefined) {
            const index = { AssignmentNo: assignmentNo, Type: type };
            const dataGrid = this.dataGrid.instance;
            const rowIndex = dataGrid.getRowIndexByKey(index);
            const rowElement = dataGrid.getRowElement(rowIndex)?.[0];
            if (rowElement) {
              const key = dataGrid.getKeyByRowIndex(rowIndex);
              // collapse details if expanded
              if (dataGrid.isRowExpanded(key)) {
                dataGrid.collapseRow(key);
              }
              // console.log('reloading assignment', rowElement);
              this.dataGrid.instance.focus(rowElement);
              rowElement?.classList.add(ret.Operation); // mark the row
              this.reloadAssignment(index);
            }
          }
        }
      },
      error: err => {
        if (err.msg) {
          notify({ message: this.translationService.instant(err.msg), shading: false }, 'warning', 3000);
        } else {
          notify({ message: this.translationService.instant('ERROR.GENERAL'), shading: false }, 'error', 3000);
        }
        const rowIndex = this.dataGrid.instance.getRowIndexByKey(err.index);
        const rowElement = this.dataGrid.instance.getRowElement(rowIndex)?.[0];
        rowElement?.classList.add(err.Operation);
        this.registerForPlanningUpdates();
      },
      complete: () => {
        // console.log('onPlanningFinished complete');
        notify(
          { message: this.translationService.instant('PROJECT_PLANNING.NOTIFICATION.PLANNING_DONE'), shading: false },
          'success',
          3000
        );
        this.registerForPlanningUpdates();
        this.showValues();
      },
    });
  }

  async reloadAssignment(index: { AssignmentNo: number; Type: AssignmentType }) {
    try {
      const getAssignmentDto: GetAssignmentDto = {
        AssignmentId: index.AssignmentNo,
        Type: index.Type,
      };

      const request: GetAssignmentsDto = {
        ApplyFilter: true,
        Assignments: [getAssignmentDto],
      };

      const assignments = await firstValueFrom(this.assignmentService.getAssignmentsByIdAndType(request));
      const i = this._assignments.findIndex(a => a.AssignmentNo == index.AssignmentNo && a.Type == index.Type);
      if (i >= 0 && assignments.length > 0 && assignments[0]) {
        this._assignments[i] = assignments[0];
        this._assignments[i].EffectiveVolume = AssignmentHelper.effectiveVolume(this._assignments[i]);
        this._assignments[i].EffectiveWeight = AssignmentHelper.effectiveWeight(this._assignments[i]);
        this._assignments[i].RemainingDispoVol =
          this._assignments[i].Volume - this.dispoVolumeInOverview(this._assignments[i]);
        this._assignments[i].RemainingDispoWeight =
          this._assignments[i].Weight - this.dispoWeightInOverview(this._assignments[i]);
        this._assignments[i].ShowEventTime = showEventTime(this._assignments[i]);
        setTimeout(() => this.showValues(), 500);
      } else {
        console.warn('Assignment not found in API response, reloading all assignments', index);
        this.getAssignments();
      }
    } catch (err) {
      console.error('Error reloading assignment', err);
      this.getAssignments();
    }
  }

  markPlanning(selection: AssignmentDto[]) {
    this.dataGrid.instance.clearSelection();
    selection.forEach(row => {
      const rowIndex = this.dataGrid.instance.getRowIndexByKey({ AssignmentNo: row.AssignmentNo, Type: row.Type });
      const rowElement = this.dataGrid.instance.getRowElement(rowIndex)?.[0];
      if (rowElement) {
        rowElement.classList.add('planning');
      }
    });
  }

  loadAssignment(event: RowDblClickEvent) {
    this.contextService.openContextView(`assignment/${event.data.AssignmentNo}/${event.data.Type!}`);
  }

  async loadAssignmentSubtableData(event: RowExpandedEvent) {
    const key = event.key as GridKey;
    const row = this._assignments.find(t => t.AssignmentNo === key.AssignmentNo && t.Type === key.Type);
    if (!row || row.subtableData !== undefined) return;
    const subtableData = await firstValueFrom(
      this.assignmentService.getAssignmentSubtableData(row.AssignmentNo!, key.Type)
    )
      .then(ret => {
        // console.log('subtableData', ret);
        return {
          ...ret,
          AssignmentNo: row.AssignmentNo,
          Type: key.Type,
          TransportLabel: row.TransportLabel,
        } as AssignmentSubtableDataDto;
      })
      .catch(err => {
        console.error('Error loading subtable data', err);
        notify({ message: this.translationService.instant('ERROR.GENERAL'), shading: false }, 'error', 3000);
        if (this.dataGrid?.instance) this.dataGrid.instance.collapseAll(-1);
      });

    if (subtableData === undefined) return;
    row.subtableData = subtableData;
    this.showValues();
  }

  async getAssignments() {
    this.dateRangeStore.range.set(this.dateRange);

    const fromDate = format(this.dateRange[0], 'yyyy-MM-dd');
    const toDate = format(this.dateRange[1], 'yyyy-MM-dd');
    // console.log(`getAssignments(${fromDate}, ${toDate})`);
    this.isLoading.set(true);
    this.assignmentService.getAssignmentsByDateRange(fromDate, toDate).subscribe({
      next: ret => {
        this._assignments = ret.map(assignment =>
          // Preserve assignment-list's original remaining-dispo semantics
          // (the disposed amount), which isOpen()/state filters rely on.
          toAssignmentExtendedDto(assignment, {
            RemainingDispoVol: assignment.Volume - this.dispoVolumeInOverview(assignment),
            RemainingDispoWeight: assignment.Weight - this.dispoWeightInOverview(assignment),
          })
        );
        this.showValues();
        this.isLoading.set(false);
        // Visual-only: adjust the shown action date of PL main assignments based on
        // their stations' fully-assigned state (fire-and-forget; refreshes on arrival).
        void this.loadEffectiveEventDatesForPlMains();
      },
      error: err => {
        notify({ message: err.statusText, shading: false }, 'error', 3000);
        console.error('getAssignments error', err);
        if (this.dataGrid?.instance) this.dataGrid.instance.collapseAll(-1);
      },
      complete: () => {
        this.isLoading.set(false);
        if (this.dataGrid?.instance) this.dataGrid.instance.collapseAll(-1);
        this.notificationService.triggerRefresh();
      },
    });
  }

  /**
   * Visual-only: for every Prime Logistic main assignment, load its stations and compute
   * the effective action date (advances past fully-assigned stations). Stored in a
   * separate field (EffectiveEventDate) - the underlying EventDate is never changed.
   */
  private async loadEffectiveEventDatesForPlMains(): Promise<void> {
    const plMains = this._assignments.filter(a => isMainPrimeLogisticAssignment(a));
    if (plMains.length === 0) {
      return;
    }

    await Promise.all(
      plMains.map(async main => {
        try {
          const subtable = await firstValueFrom(
            this.assignmentService.getAssignmentSubtableData(main.AssignmentNo!, main.Type!)
          );
          const effective = effectivePrimeLogisticEventDate(main, subtable?.PlStations);
          if (effective) {
            main.EffectiveEventDate = effective;
          }
        } catch (err) {
          console.error('Error loading PL stations for effective event date', err);
        }
      })
    );

    this.showValues();
  }

  /**
   * Visual-only cell value for the action date column. For a Prime Logistic main
   * assignment, shows the effective event date (advanced past fully-assigned stations);
   * otherwise the original EventDate. Does not change the underlying data.
   */
  calculateDisplayEventDate = (rowData: AssignmentExtendedDto): Date | null => {
    const value = rowData.EffectiveEventDate ?? rowData.EventDate;
    return value ? new Date(value) : null;
  };

  visibilityKey = (assignment: AssignmentExtendedDto): string => `${assignment.AssignmentNo}/${assignment.Type}`;

  async onChangeVisibilityOfAssignment(assignment: AssignmentExtendedDto): Promise<void> {
    const assignmentNo = assignment.AssignmentNo;
    const type = assignment.Type;
    if (assignmentNo === undefined || type === undefined) {
      return;
    }
    // one toggle per assignment at a time, otherwise out-of-order responses can leave UI and server out of sync
    await this.visibilityBusy.run(this.visibilityKey(assignment), async () => {
      const next = assignment.FilterState === 1 ? 0 : 1;
      const prev = assignment.FilterState;

      // optimistic update
      assignment.FilterState = next;
      this.showValues();

      try {
        await firstValueFrom(
          this.assignmentService.updateAssignmentFilterState(assignmentNo, type, { FilterState: next as 0 | 1 })
        );
      } catch (err) {
        // rollback
        assignment.FilterState = prev;
        this.showValues();
        notify({ message: this.translationService.instant('ERROR.GENERAL'), shading: false }, 'error', 3000);
        console.error('updateAssignmentFilterState failed', err);
      }
    });
  }

  showValues() {
    let data = this._assignments;

    if (this.selectedStates.length > 0) {
      data = data.filter(t => this.selectedStates.some(s => this.states.find(st => st.id === s)?.filter(t) ?? false));
    }

    const Hidden = 2; // ausgeblendet
    if (!this.selectedStates.includes(Hidden)) {
      data = data.filter(t => this.states[Hidden].filter(t) === false); // invert the filter
    }

    // apply info filters
    if (this.selectedInfoFilters.length > 0) {
      data = data.filter(t =>
        this.selectedInfoFilters.some(f => this.infoFilters.find(i => i.id === f)?.filter(t) ?? false)
      );
    }

    this.changeDetectorRef.markForCheck();
    this.assignments.set(data);
  }

  calculateStats() {
    const assignments = this.dataGrid.instance.getDataSource().items();
    this.stats = {
      totalDeliveryWeight: assignments.reduce((p, c) => p + (AssignmentHelper.isDelivery(c.Type) ? c.Weight! : 0), 0),
      totalDeliveryVolume: assignments.reduce((p, c) => p + (AssignmentHelper.isDelivery(c.Type) ? c.Volume! : 0), 0),
      totalPickupWeight: assignments.reduce(
        (p, c) =>
          p +
          (c.Type === AssignmentType.AssignmentPickup || c.Type === AssignmentType.PickupRentalPurchasingOrder
            ? c.Weight!
            : 0),
        0
      ),
      totalPickupVolume: assignments.reduce(
        (p, c) =>
          p +
          (c.Type === AssignmentType.AssignmentPickup || c.Type === AssignmentType.PickupRentalPurchasingOrder
            ? c.Volume!
            : 0),
        0
      ),
    };
    this.changeDetectorRef.detectChanges();
  }

  returnValueWithFarAway(rowData: AssignmentExtendedDto): SortKey {
    const col = this as Column;
    const value = rowData[col.dataField as keyof AssignmentExtendedDto] ?? '';
    return {
      value: value,
      farAway: rowData.TransportLabel === AssignmentDto.TransportLabelEnum.FarAway,
      kind: 'sortkey',
    };
  }

  // Using the values prepared by the 'returnValueWithFarAway' function, do the custom sorting
  sortFarAwayFirst(v1: SortKey, v2: SortKey) {
    const self = this as Column;
    const inverter = self.sortOrder === 'asc' ? 1 : -1;

    if (v1?.kind !== 'sortkey') {
      v1 = { kind: 'sortkey', farAway: false, value: v1 ?? '' };
    }

    if (v2?.kind !== 'sortkey') {
      v2 = { kind: 'sortkey', farAway: false, value: v2 ?? '' };
    }

    if (!v1 && !v2) return 0;
    if (v1 && !v2) return -1 * inverter;
    if (!v1 && v2) return 1 * inverter;
    if (v1.farAway && !v2.farAway) return -1 * inverter;
    if (!v1.farAway && v2.farAway) return 1 * inverter;
    let result: number;

    if (typeof v1.value === 'number' && typeof v2.value === 'number') {
      result = v2.value - v1.value;
    } else if (v1.value instanceof Date && v2.value instanceof Date) {
      result = v2.value.getTime() - v1.value.getTime();
    } else {
      result = v1.value.toString().localeCompare(v2.value.toString());
    }
    return result;
  }

  onTreeViewReady(e: DxTreeViewTypes.ContentReadyEvent) {
    this.updateSelection(e.component);
  }

  onTreeViewSelectionChanged(e: DxTreeViewTypes.ItemSelectionChangedEvent) {
    this.selectedStates = e.component.getSelectedNodeKeys();
    this.showValues();
  }

  onDropDownBoxValueChanged() {
    this.updateSelection(this.treeView?.instance);
  }

  onInfoTreeViewReady(e: DxTreeViewTypes.ContentReadyEvent) {
    this.updateInfoSelection(e.component);
  }

  onInfoTreeViewSelectionChanged(e: DxTreeViewTypes.ItemSelectionChangedEvent) {
    this.selectedInfoFilters = e.component.getSelectedNodeKeys();

    this.showValues();
  }

  onInfoFilterValueChanged() {
    this.updateInfoSelection(this.infoTreeView?.instance);
  }

  updateInfoSelection(treeView: DxTreeViewComponent['instance']) {
    if (!treeView) return;

    if (!this.selectedInfoFilters) {
      treeView.unselectAll();
      return;
    }

    this.selectedInfoFilters?.forEach(value => {
      treeView.selectItem(value);
    });
  }

  updateSelection(treeView: DxTreeViewComponent['instance']) {
    if (!treeView) return;

    if (!this.selectedStates) {
      treeView.unselectAll();
    }

    this.selectedStates?.forEach(value => {
      treeView.selectItem(value);
    });
  }

  updateSelectionOrder(e: SelectionChangedEvent) {
    // console.log('updateSelectionOrder', e);
    // keep a list of the selected rows in the order they were selected (for planning)
    this.selectedRows = e.selectedRowsData;

    // Restriction: at most ONE PrimeLogistics assignment may be selected at a time.
    // Two PL assignments cannot be planned simultaneously. Normal (non-PL) assignments
    // are unrestricted and may be combined with a single PL assignment.
    const plRows = this.selectedRows.filter(row => this.isMainPrimeLogisticAssignment(row));
    if (plRows.length > 1) {
      // Keep the first-selected PL row, deselect the rest (the newly added PL ones).
      const grid = e.component;
      const plRowsToDeselect = plRows.slice(1);
      const keysToDeselect = plRowsToDeselect.map(row => grid.keyOf(row));

      grid.deselectRows(keysToDeselect);

      notify(
        {
          message: this.translationService.instant('ASSIGNMENT.NOTIFICATION.ONLY_ONE_PRIME_LOGISTIC'),
          shading: false,
        },
        'warning',
        3000
      );
      // deselectRows triggers updateSelectionOrder again with the corrected selection.
      return;
    }

    // Track the EventDate from the first selected row for highlighting matching rows
    if (this.selectedRows.length > 0 && this.selectedRows[0].EventDate) {
      this.firstSelectedRowDate = new Date(this.selectedRows[0].EventDate);
      this.firstSelectedRowDate.setHours(0, 0, 0, 0); // Normalize to start of day for comparison
    } else {
      this.firstSelectedRowDate = null;
    }

    // Refresh all visible rows to apply new styling
    this.updateRowHighlighting();
  }

  updateRowHighlighting() {
    if (!this.dataGrid?.instance) return;

    const dataSource = this.dataGrid.instance.getDataSource();
    if (!dataSource) return;

    const visibleRows = this.dataGrid.instance.getVisibleRows();

    visibleRows.forEach(row => {
      if (row.rowType === 'data') {
        const rowElement = this.dataGrid.instance.getRowElement(row.rowIndex)?.[0];
        if (rowElement) {
          // Remove the class first
          rowElement.classList.remove('same-date-row');

          // Add the class if dates match
          if (this.firstSelectedRowDate && row.data.EventDate) {
            const rowDate = new Date(row.data.EventDate);
            rowDate.setHours(0, 0, 0, 0);

            if (rowDate.getTime() === this.firstSelectedRowDate.getTime()) {
              rowElement.classList.add('same-date-row');
            }
          }
        }
      }
    });
  }

  // Hauptauftrag der Prime Logistic erkennen
  isMainPrimeLogisticAssignment(data: AssignmentDto): boolean {
    return isMainPrimeLogisticAssignment(data);
  }

  onSearchAssignmentClick(): void {
    this.showSearchAssignmentDialog.set(true);
  }
  onSearchAssignmentClose(): void {
    this.showSearchAssignmentDialog.set(false);
  }

  isFarAway(data: AssignmentExtendedDto): boolean {
    return isFarAway(data);
  }

  isMiWarning(data: AssignmentExtendedDto): boolean {
    return isMiWarning(data);
  }

  isMiOk(data: AssignmentExtendedDto): boolean {
    return isMiOk(data);
  }

  isMiProbSolutions(data: AssignmentExtendedDto): boolean {
    return isMiProbSolutions(data);
  }
}
